<template>
  <div data-testid="grid_sequence_completion_conditions_edit">
    <draggable
      v-model="local_value"
      handle=".condition-drag-handle"
      :disabled="readonly"
      class="no-drag"
      @end="$emit('dragged')"
    >
      <v-sheet
        v-for="(completion, index) in local_value"
        :key="index"
        outlined
        rounded
        class="pa-3 mb-3"
        data-testid="grid_sequence_completion_conditions_edit_transition"
      >
        <v-row dense align="center" class="mb-2">
          <v-col v-if="show_priority" cols="auto">
            <v-icon v-if="!readonly" class="condition-drag-handle" style="cursor: move">drag_indicator</v-icon>
            <strong>{{ $t("grid_sequence_completion_condition.priority", { number: index + 1 }) }}</strong>
          </v-col>
          <v-col v-if="!is_never(completion.condition)" cols="12" sm="4">
            <v-select
              :value="completion.transition_mode || 'delayed'"
              :items="transition_mode_options"
              :label="$t('grid_sequence_completion_condition.transition_mode')"
              dense
              outlined
              hide-details
              :disabled="readonly"
              data-testid="grid_sequence_completion_conditions_edit_transition_mode"
              @change="set_transition_mode(index, $event)"
            />
          </v-col>
          <v-col v-if="show_target_selector && !is_never(completion.condition)">
            <v-select
              :value="completion.target_step"
              :items="target_options"
              :label="$t('grid_sequence_completion_condition.target_step')"
              :placeholder="$t('grid_sequence_completion_condition.select_target')"
              :rules="[(value) => value !== null || $t('grid_sequence_completion_condition.target_required')]"
              dense
              outlined
              hide-details="auto"
              :disabled="readonly"
              data-testid="grid_sequence_completion_conditions_edit_target"
              @change="set_target(index, $event)"
            />
          </v-col>
          <v-col v-if="!readonly && allow_remove_transition" cols="auto">
            <v-btn icon color="red" :disabled="local_value.length === 1" @click="remove_condition(index)">
              <v-icon>delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <GridSequenceCompletionConditionEdit
          :value="completion.condition"
          :fields="fields"
          :completed_exercises="completed_exercises"
          :readonly="readonly"
          @input="set_condition(index, $event)"
        />
      </v-sheet>
    </draggable>

    <v-btn
      v-if="!readonly && allow_add_transition"
      small
      text
      color="primary"
      class="no-drag"
      data-testid="grid_sequence_completion_conditions_edit_add_transition"
      @click="add_condition"
    >
      <v-icon left>add</v-icon>{{ $t("grid_sequence_completion_condition.add_transition") }}
    </v-btn>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import {
  defaultComparison,
  isNeverCompletionCondition,
  sequenceStepTitle,
} from "../classes/grid_sequence";
import GridSequenceCompletionConditionEdit from "./GridSequenceCompletionConditionEdit.vue";

export default {
  name: "GridSequenceCompletionConditionsEdit",
  components: { draggable, GridSequenceCompletionConditionEdit },
  props: {
    value: { type: Array, required: true },
    fields: { type: Object, required: true },
    completed_exercises: { type: Array, required: true },
    steps: { type: Array, required: true },
    step_id: { type: String, required: true },
    readonly: { type: Boolean, default: false },
    show_target_selector: { type: Boolean, default: true },
    show_priority: { type: Boolean, default: true },
    allow_add_transition: { type: Boolean, default: true },
    allow_remove_transition: { type: Boolean, default: true },
  },
  computed: {
    local_value: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    target_options() {
      return [
        { text: this.$t("grid_sequence_completion_condition.complete"), value: "complete" },
        ...this.steps
          .map((step, index) => ({
            text: this.step_title(step, index),
            value: step.id,
          }))
          .filter((option) => option.value !== this.step_id),
      ];
    },
    transition_mode_options() {
      return [
        { text: this.$t("grid_sequence_completion_condition.delayed"), value: "delayed" },
        { text: this.$t("grid_sequence_completion_condition.immediate"), value: "immediate" },
      ];
    },
  },
  methods: {
    step_title(step, index) {
      const fallback = this.$t("grid_sequence_step_list.step", { number: index + 1 });
      return sequenceStepTitle(step, fallback);
    },
    default_condition() {
      if (this.completed_exercises.length) {
        return {
          type: "exercise_completed_count",
          exercise_id: this.completed_exercises[0].value,
          count: 1,
        };
      }
      const field = Object.keys(this.fields)[0];
      return field ? defaultComparison(field, this.fields[field]) : { type: "always" };
    },
    add_condition() {
      this.$emit("input", [
        ...this.local_value,
        {
          condition: this.default_condition(),
          target_step: null,
          transition_mode: "delayed",
        },
      ]);
    },
    set_condition(index, condition) {
      const value = this.local_value.slice();
      value.splice(index, 1, {
        ...value[index],
        condition,
        target_step: isNeverCompletionCondition(condition) ? null : value[index].target_step,
      });
      this.$emit("input", value);
    },
    is_never(condition) {
      return isNeverCompletionCondition(condition);
    },
    set_target(index, target_step) {
      const value = this.local_value.slice();
      value.splice(index, 1, { ...value[index], target_step });
      this.$emit("input", value);
    },
    set_transition_mode(index, transition_mode) {
      const value = this.local_value.slice();
      value.splice(index, 1, { ...value[index], transition_mode });
      this.$emit("input", value);
    },
    remove_condition(index) {
      if (this.local_value.length === 1) return;
      const value = this.local_value.slice();
      value.splice(index, 1);
      this.$emit("input", value);
    },
  },
};
</script>
